import { httpClient } from './http-client';

/**
 * Household retirement model (issue #32): /retirement/household.
 *
 * The model is plain JSON numbers (rates in percent, amounts in today's
 * dollars) because it is a set of editable assumptions, not ledger money.
 * Projection outputs are whole dollars. Everything is a projection, not
 * advice; every response carries `disclaimer`.
 */

export type Role = 'adult' | 'child';

export interface CPPSettings {
  mode: 'estimate' | 'amount';
  monthly_at_65?: number;
  start_age?: number;
  career_start_age?: number;
}

export interface OASSettings {
  start_age?: number;
  residency_years?: number;
}

export interface Person {
  id: string;
  name: string;
  dob: string;
  role: Role;
  retirement_age?: number;
  cpp?: CPPSettings;
  oas?: OASSettings;
  user_id?: string;
  /** Person plan: share of joint assets/debts, and of household spending (default 50). */
  joint_share_pct?: number | null;
  spending_share_pct?: number | null;
}

export interface Leave {
  label?: string;
  start: string;
  topup_end?: string;
  topup_pct?: number;
  topup_amount_monthly?: number;
  ei_biweekly: number;
  ei_end?: string;
  return: string;
}

export interface Income {
  id: string;
  person_id: string;
  label?: string;
  annual_gross: number;
  growth_pct?: number | null;
  start_date?: string;
  end_date?: string;
  leaves?: Leave[];
}

export interface ChildCost {
  person_id: string;
  monthly?: number;
  annual?: number;
  from_age: number;
  to_age: number;
  /** When the cost actually began (YYYY-MM), replacing from_age. */
  from_date?: string;
}

export interface ExpenseItem {
  label: string;
  monthly: number;
  start_date?: string;
  end_date?: string;
  inflate?: boolean;
}

export interface Expenses {
  baseline_annual_override?: number | null;
  retirement_spending_pct: number;
  childcare: ChildCost[];
  activities: ChildCost[];
  post_secondary: ChildCost[];
  resp_annual_per_child: number;
  items?: ExpenseItem[];
}

export interface DebtOverride {
  account_id?: string;
  name?: string;
  balance?: number | null;
  rate_pct?: number | null;
  monthly_payment?: number | null;
  exclude?: boolean;
  kind?: 'heloc' | 'mortgage' | 'auto' | 'loan';
  label?: string;
  rate_type?: 'fixed' | 'variable';
  spread_pct?: number | null;
  term_start?: string;
  term_end?: string;
  payment_type?: 'fixed' | 'adjustable';
  amortization_months?: number;
  renewal?: 'same_spread' | 'same_rate';
  notes?: string;
  paid_off_date?: string;
}

export type EventKind = 'vehicle_sale' | 'vehicle_purchase' | 'lump_sum_in' | 'lump_sum_out' | 'loan_payoff' | 'custom';

/** A planned (or remembered) one-off in the household model (issue #39). */
export interface PlanEvent {
  id: string;
  /** YYYY-MM or YYYY-MM-DD. */
  date: string;
  window_start?: string;
  window_end?: string;
  kind: EventKind;
  label: string;
  /** Sale proceeds / purchase price / lump sum; custom is signed (+ in). */
  amount?: number | null;
  amount_assumed?: boolean;
  account_id?: string;
  asset_name?: string;
  depreciation_pct?: number | null;
  funding?: 'cash';
  notes?: string;
}

export interface TimelineItem {
  id: string;
  date: string;
  year: number;
  kind: string;
  icon: string;
  label: string;
  amount?: number;
  source: 'derived' | 'user' | 'data';
  link?: string;
  past: boolean;
  event_id?: string;
  window?: string;
  amount_assumed?: boolean;
  person_id?: string;
}

export interface TimelineResponse {
  as_of: string;
  items: TimelineItem[];
  configured: boolean;
  disclaimer: string;
}

/** A revolving line (HELOC). Unused (balance 0) it is available credit only. */
export interface CreditLine {
  name: string;
  limit: number | null;
  spread_pct: number | null;
  balance: number;
  status: 'unused' | 'drawn';
  notes?: string;
  /** Readvanceable: the mortgage whose repaid principal becomes available. */
  readvance_of_account_id?: string;
  limit_basis?: { amount: number; as_of: string; source: 'user' | 'bank' } | null;
  limit_cap?: number | null;
}

export interface ClassReturn {
  mean_pct: number;
  stdev_pct: number;
}

export interface Assumptions {
  inflation_pct: number;
  salary_growth_pct: number;
  returns: Record<string, ClassReturn>;
  withdrawal_rate_pct: number;
  withdrawal_strategy: 'fixed' | 'guardrails';
  end_age: number;
  success_target_pct: number;
  resp_return_pct: number;
  default_loan_rate_pct: number;
  prime_pct: number;
  prime_shift_pct?: number;
}

export interface HouseholdModel {
  people: Person[];
  incomes: Income[];
  expenses: Expenses;
  debts?: DebtOverride[];
  credit_lines?: CreditLine[];
  events?: PlanEvent[];
  assets: {
    home_value_override?: number | null;
    home_growth_pct?: number | null;
    vehicle_depreciation_pct: number;
    investable_override?: number | null;
  };
  contributions: { surplus_sweep_pct?: number; extra_monthly?: number };
  assumptions: Assumptions;
  notes?: string;
}

export interface Debt {
  account_id?: string;
  name: string;
  kind: string;
  balance: number;
  rate_pct: number;
  rate_assumed?: boolean;
  monthly_payment: number;
  payment_source?: string;
  label?: string;
  rate_type?: string;
  spread_pct?: number | null;
  term_start?: string;
  term_end?: string;
  payment_type?: string;
  amortization_months?: number;
  amortization_source?: string;
  renewal?: string;
  payment_history?: string;
  notes?: string;
  rate_implied?: boolean;
  rate_note?: string;
  term_note?: string;
}

export interface DebtScenario {
  debt: string;
  prime_shift_pct: number;
  rate_pct: number;
  payment: number;
  payoff_date?: string;
  months: number;
  total_interest: number;
  never_pays_off?: boolean;
}

export interface AvailableCredit {
  reserves: number;
  credit_lines: { name: string; limit: number | null; limit_prompt?: string; rate_pct: number; spread_pct: number;
    spread_assumed?: boolean; balance: number; status: string; readvanceable?: boolean; available?: number;
    growth_monthly?: number; limit_cap?: number | null; basis_note?: string; summary: string;
    path?: { year: number; available: number }[];
    scenarios?: { prime_shift_pct: number; growth_monthly: number; available_in_5y: number; fully_available?: string }[] }[];
  note: string;
}

export interface Baseline {
  annual: number;
  window_start: string;
  window_end: string;
  included: Record<string, number>;
  excluded: Record<string, { amount: number; reason: string }>;
  total_categorised: number;
  card_payments?: number;
  card_itemised?: number;
}

export interface Inputs {
  as_of: string;
  currency: string;
  investable: Record<string, number>;
  pool_remaining: number;
  pool_monthly: number;
  pool_mix: Record<string, number>;
  pool_target_end?: string;
  target_mix: Record<string, number>;
  reserves: number;
  buckets: Record<string, number>;
  resp: number;
  operating_cash: number;
  home_value: number;
  vehicles: number;
  debts: Debt[] | null;
  other_liabilities: number;
  baseline: Baseline;
  allocation_source: string;
  notes: string[];
  accounts?: { account_id: string; name: string; kind: string; bucket?: string; balance: number; joint?: boolean }[];
  paid_off_debts?: { account_id: string; name: string; date?: string }[];
}

export interface ModelResponse {
  configured: boolean;
  enabled: boolean;
  version: number;
  model: HouseholdModel | null;
  updated_at?: string;
  starter?: HouseholdModel;
  inputs?: Inputs;
  disclaimer: string;
}

export interface Year {
  year: number;
  months: number;
  ages: Record<string, number>;
  phase: string;
  employment: number;
  ei: number;
  cpp: number;
  oas: number;
  ccb: number;
  tax: number;
  after_tax_income: number;
  core_spending: number;
  child_costs: number;
  education: number;
  debt_payments: number;
  resp_contributions: number;
  other_items: number;
  total_spending: number;
  contributions: number;
  withdrawals: number;
  pool_deployed: number;
  investable: number;
  resp: number;
  home_value: number;
  vehicles: number;
  debt: number;
  home_equity: number;
  net_worth: number;
  deflator: number;
  investable_real: number;
  net_worth_real: number;
  expected_return_pct: number;
  stdev_pct: number;
}

export interface Milestone {
  date: string;
  kind: string;
  label: string;
  person_id?: string;
  past?: boolean;
}

export interface AgePoint {
  person_id: string;
  name: string;
  age: number;
  date: string;
  investable: number;
  investable_real: number;
  net_worth: number;
  net_worth_real: number;
}

export interface Summary {
  start_investable: number;
  start_net_worth: number;
  start_debt: number;
  at_65: AgePoint[];
  at_retirement: AgePoint[];
  household_retirement: string;
  investable_at_household_retirement: number;
  investable_at_household_retirement_real: number;
  first_year_draw_real: number;
  retirement_core_spending: number;
  taxable_withdrawal_share: number;
  end_date: string;
  end_investable: number;
  end_investable_real: number;
  end_net_worth: number;
  end_net_worth_real: number;
  depleted_on?: string;
  baseline_annual: number;
}

export interface Projection {
  as_of: string;
  currency: string;
  years: Year[];
  milestones: Milestone[];
  summary: Summary;
  debts: Debt[] | null;
  warnings: string[];
  debt_sensitivity: DebtScenario[] | null;
  prime_pct: number;
  prime_shift_pct: number;
  available_credit: AvailableCredit;
}

export interface Readiness {
  household_retirement: string;
  withdrawal_rate_pct: number;
  projected: number;
  required: number;
  gap: number;
  on_track: boolean;
  first_year_draw: number;
  rule_sustainable_spending: number;
  levers: { extra_monthly?: number; retire_later_years?: number; spend_less_pct?: number };
  explanation: string;
  backstop?: string;
}

export interface ScopeInfo {
  person_id: string;
  name: string;
  user_id?: string;
  joint_share_pct: number;
  spending_share_pct: number;
  accounts: { name: string; kind: string; bucket?: string; balance: number; owner: 'own' | 'joint' | 'other' | 'education'; share_pct: number; counted: number }[];
  counted: Record<string, number>;
  notes: string[];
}

/** 'household' or 'person:<person_id>'. */
export type PlanScope = string;

export interface ProjectResponse {
  model_version: number;
  projection: Projection;
  readiness: Readiness;
  inputs: Inputs;
  scope?: ScopeInfo;
  disclaimer: string;
}

export interface YearBand {
  year: number;
  p10: number;
  p25: number;
  p50: number;
  p75: number;
  p90: number;
  deterministic: number;
}

export interface Simulation {
  paths: number;
  withdrawal_strategy: string;
  success_rate: number;
  investable: YearBand[];
  investable_real: YearBand[];
  net_worth: YearBand[];
  net_worth_real: YearBand[];
  sustainable_spending: number;
  sustainable_spending_pct: number;
  success_target_pct: number;
  at_retirement_real: YearBand;
  median_spending_scale: number;
  p10_spending_scale: number;
  depletion_year_p10?: number;
}

export interface SimulateResponse {
  model_version: number;
  simulation: Simulation;
  summary: Summary;
  readiness: Readiness;
  scope?: ScopeInfo;
  disclaimer: string;
}

export interface ModelVersion {
  version: number;
  enabled: boolean;
  model: HouseholdModel;
  note?: string;
  created_at: string;
}

export interface ReadinessCardResponse {
  configured: boolean;
  model_version?: number;
  readiness?: Readiness;
  at_65?: AgePoint[];
  household_retirement?: string;
  currency?: string;
  disclaimer: string;
}

const BASE = '/retirement/household';
const q = (scope: PlanScope) => (scope && scope !== 'household' ? `?scope=${encodeURIComponent(scope)}` : '');
// Same envelope handling as the other services: some deployments wrap in {data}.
const unwrap = <T,>(raw: any): T => ((raw && raw.data) || raw) as T;

export const HouseholdRetirementService = {
  async get(withInputs = true): Promise<ModelResponse> {
    return unwrap(await httpClient.get<any>(withInputs ? BASE : `${BASE}?inputs=false`));
  },
  async save(model: HouseholdModel, note = '', enabled = true): Promise<ModelResponse> {
    return unwrap(await httpClient.put<any>(BASE, { model, note, enabled }));
  },
  async versions(): Promise<ModelVersion[]> {
    const raw: any = unwrap(await httpClient.get<any>(`${BASE}/versions`));
    return raw.versions ?? [];
  },
  async project(model?: HouseholdModel, scope: PlanScope = 'household'): Promise<ProjectResponse> {
    return unwrap(await httpClient.post<any>(`${BASE}/project${q(scope)}`, model ? { model } : {}));
  },
  async simulate(model?: HouseholdModel, scope: PlanScope = 'household'): Promise<SimulateResponse> {
    return unwrap(await httpClient.post<any>(`${BASE}/simulate${q(scope)}`, model ? { model } : {}));
  },
  async readiness(scope: PlanScope = 'household'): Promise<ReadinessCardResponse> {
    return unwrap(await httpClient.get<any>(`${BASE}/readiness${q(scope)}`));
  },
  async events(): Promise<PlanEvent[]> {
    const raw: any = unwrap(await httpClient.get<any>(`${BASE}/events`));
    return raw.events ?? [];
  },
  async addEvent(e: Omit<PlanEvent, 'id'> & { id?: string }): Promise<PlanEvent> {
    const raw: any = unwrap(await httpClient.post<any>(`${BASE}/events`, e));
    return raw.event;
  },
  async updateEvent(id: string, e: PlanEvent): Promise<PlanEvent> {
    const raw: any = unwrap(await httpClient.put<any>(`${BASE}/events/${encodeURIComponent(id)}`, e));
    return raw.event;
  },
  async deleteEvent(id: string): Promise<void> {
    await httpClient.delete(`${BASE}/events/${encodeURIComponent(id)}`);
  },
  async timeline(): Promise<TimelineResponse> {
    return unwrap(await httpClient.get<any>('/timeline'));
  },
  async saveAnalysis(model?: HouseholdModel, scope: PlanScope = 'household'): Promise<{ analysis_id: string; title: string }> {
    return unwrap(await httpClient.post<any>(`${BASE}/analysis${q(scope)}`, model ? { model } : {}));
  },
};
